import { useMemo, useState } from 'react';
import Popup from 'reactjs-popup';
import { capitalizeWord } from 'utils/stringUtils';
import { usePerms } from 'contexts/PermsContext';
import TextToggle from 'components/UI/TextToggle';
import { useSearchParams } from 'react-router-dom';
import SearchBar from 'components/SearchBar/SearchBar';
import BaseTable from 'components/Tables/BaseTable';
import { EllipsisMenu } from '..';
import { Button, Container } from 'react-bootstrap';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import BaseAccordion from 'components/Accordions/BaseAccordion';
import {
  deleteGlobalAction,
  setGlobalActionEnabled,
} from 'services/globalActions/globalActions.service';

const CurrentGlobalActions = ({ data, setData, setInitialData, setShowNewGlobalAction }) => {
  const [params] = useSearchParams();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [rowIdEllipsisOpen, setRowIdEllipsisOpen] = useState({});
  const [mobileRowIdEllipsisOpen, setMobileRowIdEllipsisOpen] = useState({});
  const perms = usePerms();

  async function localSetGlobalActionEnabled(id, value) {
    //Only continue if the value is different
    const action = data.find((check) => {
      if (check.id === id) {
        return check;
      }
    });
    if (action.status === value) return action;

    try {
      const res = await setGlobalActionEnabled(id, value);
      if (res.status === 200) {
        setData(
          data.map((action) => {
            if (action.id === id) {
              return { ...action, status: value };
            }
            return action;
          }),
        );
      }
    } catch (err) {
      console.error('Error setting global action', err);
    }
  }

  const searchFilter = (globalAction) => {
    if (!globalAction) return false;

    const search = params.get('search');

    if (search) {
      return (
        globalAction.title
          ?.toLowerCase()
          .replace(/\s/g, '')
          .includes(search?.toLowerCase().replace(/\s/g, '')) ||
        globalAction.actionName
          ?.toLowerCase()
          .replace(/\s/g, '')
          .includes(search?.toLowerCase().replace(/\s/g, '')) ||
        globalAction.resolutionName
          ?.toLowerCase()
          .replace(/\s/g, '')
          .includes(search?.toLowerCase().replace(/\s/g, '')) ||
        globalAction.triggerName
          ?.toLowerCase()
          .replace(/\s/g, '')
          .includes(search?.toLowerCase().replace(/\s/g, ''))
      );
    } else return true;
  };

  const onConfirmDelete = () => {
    deleteGlobalAction(selectedItem.id)
      .then(() => {
        setConfirmOpen(false);
        setSelectedItem(null);
        setData(data.filter((item) => item.id !== selectedItem.id));
      })
      .catch((err) => {
        console.error('Error deleting global action', err);
      });
  };

  const onEdit = (globalAction) => {
    setInitialData(globalAction);
    setShowNewGlobalAction(true);
  };

  const onRemove = (globalAction) => {
    setSelectedItem(globalAction);
    setConfirmOpen(true);
  };

  const columns = useMemo(() => {
    return [
      {
        header: 'ID',
        accessorKey: 'id',
        Cell: ({ cell }) => {
          const id = cell.getValue();
          return <div className="text-white fs-6">{id}</div>;
        },
      },
      {
        header: 'Current State',
        accessorKey: 'state',
        Cell: ({ cell }) => {
          const state = cell.getValue();
          return <div className="text-white fw-bold fs-6">{state}</div>;
        },
      },
      {
        header: 'Title',
        accessorKey: 'title',
        Cell: ({ cell }) => {
          const title = cell.getValue();
          return (
            <Popup
              on="hover"
              position={'top center'}
              trigger={<div className="text-white">{title}</div>}
            >
              <div className="text-white p-2 rounded-2 bg-primary-shadow">{title}</div>
            </Popup>
          );
        },
      },
      {
        header: 'Action',
        accessorKey: 'action',
        Cell: ({ cell }) => {
          const action = cell.getValue();
          return <div className="text-white fs-6">{action}</div>;
        },
      },
      {
        header: 'Recent Activity',
        accessorKey: 'recentActivity',
        Cell: ({ cell }) => {
          const recentActivity = cell.getValue();
          return <div className="text-white fs-6">{recentActivity}</div>;
        },
      },
      {
        header: 'Status',
        accessorKey: 'status',
        Cell: ({ row, cell }) => {
          const status = cell.getValue();
          const id = row.original.id;
          return (
            <div className="d-flex align-items-center gap-3">
              {perms.testPermission([8, 9, 10, 11, 12, 13], ['Enable-DisableGlobalActions']) ? (
                <TextToggle
                  options={['Offline', 'Online']}
                  value={status}
                  setValue={(value) => localSetGlobalActionEnabled(id, value)}
                />
              ) : (
                <div
                  className="p-1 text-center rounded-3 text-white"
                  style={{
                    backgroundColor: status ? '#01B574' : '',
                    border: status ? '0px' : '1px solid #ccc',
                    width: 80,
                  }}
                >
                  {status ? 'Online' : 'Offline'}
                </div>
              )}
              <EllipsisMenu
                options={[
                  {
                    component: (
                      <>
                        {perms.testPermission(
                          [8, 9, 10, 11, 12, 13],
                          ['Create-EditGlobalActions'],
                        ) && (
                          <Button
                            className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                            onClick={() => onEdit(row.original.item)}
                            variant="secondary"
                          >
                            Edit
                          </Button>
                        )}
                      </>
                    ),
                  },
                  {
                    component: (
                      <>
                        {perms.testPermission([8, 9, 10, 11, 12, 13], ['DeleteGlobalActions']) && (
                          <Button
                            className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                            onClick={() => onRemove(row.original.item)}
                            variant="danger"
                          >
                            Remove
                          </Button>
                        )}
                      </>
                    ),
                  },
                ]}
                open={rowIdEllipsisOpen[id]}
                containerClassname="bg-transparent"
                onOpen={() =>
                  setRowIdEllipsisOpen({
                    ...rowIdEllipsisOpen,
                    [id]: true,
                  })
                }
                onClose={() =>
                  setRowIdEllipsisOpen({
                    ...rowIdEllipsisOpen,
                    [id]: false,
                  })
                }
              />
            </div>
          );
        },
        size: 250,
      },
    ];
  }, [onEdit, onRemove, rowIdEllipsisOpen, perms]);

  const rows = useMemo(() => {
    if (data.length === 0) return [];

    return data.filter(searchFilter).map((item, index) => ({
      id: item.id,
      state: capitalizeWord(item.state),
      title: item.title,
      action: item.actionName,
      recentActivity: new Date().toLocaleString([], {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
      }), // Placeholder for recent activity
      status: item.status,
      item: item,
    }));
  }, [data, params]);

  return (
    <Container fluid className="d-flex flex-column w-100 h-100 pt-3">
      <div>
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center">
          <div className="fw-bold fs-4">Global Actions</div>
          <div>
            <SearchBar className="bg-primary-highlight rounded-3" />
          </div>
        </div>
      </div>
      {/* Large screen width uses BaseTable */}
      <div className="d-none d-lg-block">
        <BaseTable
          customStyles={{ cell: { borderBottom: '1px solid #56577A' } }}
          columns={columns}
          rows={rows}
        />
      </div>
      {/* Small screen width uses Accordion */}
      <div className="d-block d-lg-none mt-3">
        {data.filter(searchFilter).map((item, index) => (
          <div
            className={index !== data.filter(searchFilter).length - 1 ? 'mb-3' : ''}
            key={item.id}
          >
            <BaseAccordion
              key={item.id}
              bodyBackground="#232B33"
              header={
                <div className="d-flex flex-column gap-2">
                  <p className="d-flex flex-column">
                    {item.title}
                    <span>{capitalizeWord(item.state)}</span>
                  </p>
                  <p
                    className={`border border-1 border-white rounded-3 d-flex align-items-center justify-content-center ${
                      item.status ? 'bg-success' : 'bg-primary-shadow'
                    }`}
                    style={{ width: 88, height: 27 }}
                  >
                    {item.status ? 'Online' : 'Offline'}
                  </p>
                </div>
              }
            >
              <div className="d-flex justify-content-between">
                <p>Trigger: {item.triggerName}</p>
                <EllipsisMenu
                  options={[
                    {
                      component: (
                        <>
                          {perms.testPermission(
                            [8, 9, 10, 11, 12, 13],
                            ['Create-EditGlobalActions'],
                          ) && (
                            <Button
                              className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                              onClick={() => onEdit(item)}
                              variant="secondary"
                            >
                              Edit
                            </Button>
                          )}
                        </>
                      ),
                    },
                    {
                      component: (
                        <>
                          {perms.testPermission(
                            [8, 9, 10, 11, 12, 13],
                            ['DeleteGlobalActions'],
                          ) && (
                            <Button
                              className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                              onClick={() => onRemove(item)}
                              variant="danger"
                            >
                              Remove
                            </Button>
                          )}
                        </>
                      ),
                    },
                  ]}
                  open={mobileRowIdEllipsisOpen[item.id]}
                  containerClassname="bg-transparent"
                  onOpen={() =>
                    setMobileRowIdEllipsisOpen({
                      ...mobileRowIdEllipsisOpen,
                      [item.id]: true,
                    })
                  }
                  onClose={() =>
                    setMobileRowIdEllipsisOpen({
                      ...mobileRowIdEllipsisOpen,
                      [item.id]: false,
                    })
                  }
                />
              </div>
              <p>Action: {item.actionName}</p>
              <p>Resolution: {item.resolutionName}</p>
              <div className="d-flex justify-content-center mt-2">
                <div style={{ width: '200px' }}>
                  {perms.testPermission([8, 9, 10, 11, 12, 13], ['Enable-DisableGlobalActions']) ? (
                    <TextToggle
                      options={['Offline', 'Online']}
                      value={item.status}
                      setValue={(value) => localSetGlobalActionEnabled(item.id, value)}
                    />
                  ) : (
                    <div
                      className="p-1 text-center rounded-3 text-white"
                      style={{
                        backgroundColor: item.status ? '#01B574' : '',
                        border: item.status ? '0px' : '1px solid #ccc',
                        width: 80,
                      }}
                    >
                      {item.status ? 'Online' : 'Offline'}
                    </div>
                  )}
                </div>
              </div>
            </BaseAccordion>
          </div>
        ))}
      </div>
      <ConfirmationModal
        show={confirmOpen}
        handleShow={() => setConfirmOpen(false)}
        bodyTxt={`Are you sure you want to delete ${selectedItem?.title}?`}
        cancelBtnTxt="Cancel"
        confirmBtnTxt="Yes"
        confirmHandler={onConfirmDelete}
      />
    </Container>
  );
};

export default CurrentGlobalActions;
